import { getAgentColor } from '../../utils/agents';
import type { AgentInfo } from '../../utils/agents';
import ChatBubble from './ChatBubble';

export type AgentStatus = 'working' | 'idle' | 'error';

interface DeskAgentProps {
  agent: AgentInfo;
  status: AgentStatus;
  x: number;    // percentage position
  y: number;    // percentage position
  isWalking: boolean;
  chatMessage?: string | null;
  pulsing?: boolean;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export default function DeskAgent({
  agent,
  status,
  x,
  y,
  isWalking,
  chatMessage,
  pulsing,
}: DeskAgentProps) {
  const bgColor = getAgentColor(agent.department);

  const statusDotColor =
    status === 'working'
      ? 'bg-success'
      : status === 'error'
        ? 'bg-error'
        : 'bg-text-muted';

  const statusDotPulse = status === 'working' ? 'animate-pulse-dot' : '';

  return (
    <div
      className="absolute flex flex-col items-center -translate-x-1/2 -translate-y-1/2 z-10"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transition: 'left 2s ease-in-out, top 2s ease-in-out',
      }}
    >
      {/* Chat bubble */}
      <ChatBubble text={chatMessage || ''} visible={!!chatMessage} />

      {/* Avatar circle */}
      <div className="relative">
        <div
          className={`
            w-7 h-7 rounded-full flex items-center justify-center
            text-[9px] font-bold text-white select-none
            ${bgColor}
            ${isWalking ? 'animate-walking' : ''}
            ${pulsing ? 'animate-pulse-dot' : ''}
            shadow-md
            transition-shadow duration-300
            hover:shadow-lg hover:ring-1 hover:ring-white/20
          `}
          title={`${agent.name} — ${agent.title}`}
        >
          {getInitials(agent.name)}
        </div>

        {/* Status dot */}
        <div
          className={`
            absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full
            border border-bg-primary
            ${statusDotColor} ${statusDotPulse}
          `}
        />
      </div>

      {/* Name label */}
      <span className="mt-0.5 text-[8px] text-text-muted truncate max-w-[56px] text-center leading-tight">
        {agent.shortName}
      </span>
    </div>
  );
}
